import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";

const URL = "https://alkoholprovet.se/kunskapsbank/kunskapsprovet-jonkoping";

const faqs = [
  { q: "Var gör jag kunskapsprovet i Jönköping?", a: "Jönköpings kommuns tillståndsenhet på Socialförvaltningen administrerar provet. Provet skrivs på plats i Juneporten eller annan utsedd lokal. Bokning sker via kommunens e-tjänst." },
  { q: "Hur lång är väntetiden i Jönköping?", a: "Normalt 3–5 veckor från ansökan till provtillfälle, men kan vara längre under sommarperioden." },
  { q: "Vad kostar kunskapsprovet i Jönköping?", a: "Ansökningsavgift för stadigvarande tillstånd är ca 9 000–11 000 kr. Avgift för omprov ligger på 1 500–2 500 kr." },
  { q: "Kan jag göra provet på engelska?", a: "Ja, Jönköpings kommun erbjuder provet på engelska. Anmäl detta i samband med ansökan." },
  { q: "Hur lång är total handläggningstid i Jönköping?", a: "Räkna med 2–4 månader från komplett ansökan till beslut för stadigvarande tillstånd." },
];

const sections = [
  { h2: "Tillståndsenheten i Jönköping", body: "Jönköpings kommun har en effektiv tillståndsenhet under Socialförvaltningen som hanterar samtliga serveringstillstånd inom kommunen. Enheten samordnar yttranden från Polisen i Region Öst, Skatteverket, Räddningstjänsten och Miljökontoret. Ansökan sker via e-tjänsten på jonkoping.se. Som residensstad i Jönköpings län har staden en stor restaurang- och hotellsektor, vilket gör att tillståndsenheten har god rutin på både små och stora ärenden." },
  { h2: "Ansökan – steg för steg", body: "1. Förbered alla dokument: bolagsbevis, hyreskontrakt, ritning, finansiering, PBI-uppgifter och verksamhetsbeskrivning.\n2. Lämna in ansökan via e-tjänsten på jonkoping.se med BankID.\n3. Betala ansökningsavgift.\n4. Boka kunskapsprov i samband med ansökan – väntetid 3–5 veckor.\n5. Skriv provet i Juneporten (90 minuter, papper, legitimation krävs).\n6. Vänta in remissvar från polis, skatteverket m.fl.\n7. Få beslut – normalt 2–4 månader från komplett ansökan." },
  { h2: "Väntetider och säsongsvariation", body: "Jönköpings handläggning är generellt snabb. Säsongsmönster:\n• Vinter (januari–mars): kortast väntetid.\n• Vår (april–maj): ökat tryck inför sommaren.\n• Sommar: hög belastning, planera 6–8 veckor extra.\n• Höst: normal nivå.\n\nElmia och stora mässor i staden kan tillfälligt höja belastningen på tillståndsenheten." },
  { h2: "Kostnader i Jönköping", body: "• Stadigvarande tillstånd: 9 000–11 000 kr.\n• Tillfälligt tillstånd: 2 000–6 000 kr.\n• Omprov kunskapsprov: 1 500–2 500 kr.\n• Årlig tillsynsavgift: 2 500–10 000 kr beroende på omsättning.\n\nAvgifter fastställs av kommunfullmäktige och justeras årligen." },
  { h2: "Praktiskt om provdagen", body: "Provet skrivs på papper under tystnad. Du har 90 minuter. Ta med giltig legitimation. Mobil, anteckningar och andra hjälpmedel är inte tillåtna. Resultatet meddelas skriftligt inom 1–2 veckor. Vid underkänt kan omprov bokas tidigast efter 14 dagar." },
  { h2: "Tips för Jönköpings-sökande", body: "Jönköping är känt för att uppskatta tydliga, kompletta ansökningar. Vanliga orsaker till fördröjning:\n• Otydlig PBI-redogörelse.\n• Brister i finansiering.\n• Saknad ritning.\n\nFörbered kunskapsprovet med minst 200 övningsfrågor och två fullängdsprov mot klockan. Vår plattform har 800 frågor uppdaterade för 2026 års regler." },
];

function Page() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Kunskapsbank", to: "/kunskapsbank" },
        { label: "Kunskapsprovet Jönköping" },
      ]}
      title="Kunskapsprovet serveringstillstånd Jönköping – guide 2026"
      intro="Allt om kunskapsprovet för serveringstillstånd i Jönköping: ansökan, väntetider, kostnader och praktiska tips. Uppdaterat för 2026 års regler."
      sections={sections}
      faqs={faqs}
      lastReviewed="2026-06-15"
      related={{
        title: "Läs vidare",
        links: [
          { to: "/serveringstillstand-jonkoping", label: "Serveringstillstånd Jönköping" },
          { to: "/kunskapsbank/kunskapsprovet-orebro", label: "Kunskapsprovet Örebro" },
          { to: "/kunskapsbank/kunskapsprovet-umea", label: "Kunskapsprovet Umeå" },
          { to: "/kunskapsbank/kunskapsprovet-vaxjo", label: "Kunskapsprovet Växjö" },
          { to: "/practice", label: "Träna på kunskapsprovet" },
        ],
      }}
      sources={[
        { label: "Jönköpings kommun – serveringstillstånd", href: "https://www.jonkoping.se/" },
        { label: "Alkohollagen (2010:1622)", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" },
      ]}
    />
  );
}

export const Route = createFileRoute("/kunskapsbank/kunskapsprovet-jonkoping")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Kunskapsprovet Jönköping – ansökan, väntetid & kostnad 2026" },
      { name: "description", content: "Allt om kunskapsprovet för serveringstillstånd i Jönköping: ansökan, väntetider, kostnader och tips. Uppdaterat 2026." },
      { property: "og:title", content: "Kunskapsprovet Jönköping – komplett guide 2026" },
      { property: "og:description", content: "Allt om kunskapsprovet för serveringstillstånd i Jönköping." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Kunskapsbank", url: "https://alkoholprovet.se/kunskapsbank" },
        { label: "Kunskapsprovet Jönköping", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Kunskapsprovet Jönköping 2026",
        description: "Komplett guide till kunskapsprovet för serveringstillstånd i Jönköping.",
        datePublished: "2026-06-15",
        dateModified: "2026-06-15",
      }),
    ],
  }),
});
